import { useState, useEffect } from "react";
import axios from "axios";
import { MapPin, RefreshCw } from "lucide-react";
import { PROGRAMME_COLORS } from "@/lib/format";
import { nzNow } from "@/lib/dates";

function ProgrammeBookingCards({ programmes, prefix }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
      {programmes.map((p) => (
        <div key={p.programme} className="rounded-xl border border-[var(--hp-border)] px-4 py-2.5" data-testid={`${prefix}-${p.programme.replace(/\s+/g, "-").toLowerCase()}`}>
          <div className="flex items-center gap-2 text-sm mb-1">
            <span className="h-2 w-2 rounded-full shrink-0" style={{ background: PROGRAMME_COLORS[p.programme] || "#94A3B8" }} />
            <span className="font-semibold text-[var(--hp-heading)]">{p.programme}</span>
            <span className="ml-auto font-mono-num font-semibold text-[var(--hp-heading)]" data-testid={`${prefix}-${p.programme.replace(/\s+/g, "-").toLowerCase()}-total`}>{p.total}</span>
          </div>
          <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-[var(--hp-muted)]">
            {p.sessions.map((s) => (
              <span key={s.sessionTypeId}>{s.name} <span className="font-mono-num font-semibold text-[var(--hp-heading)]">{s.count}</span></span>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function TodaysBookings({ api, onOpenRoll }) {
  const [date, setDate] = useState(nzNow().toLocaleDateString("en-CA"));
  const [data, setData] = useState(null);
  const [fetchedAt, setFetchedAt] = useState(null);
  const load = () => axios.get(`${api}/bookings/today-summary`, { params: { date } }).then((r) => { setData(r.data); setFetchedAt(new Date()); }).catch(() => {});
  useEffect(() => { load(); }, [api, date]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!data) return null;
  const dayLabel = new Date(data.date + "T12:00:00").toLocaleDateString("en-NZ", { weekday: "long", day: "numeric", month: "long" });
  const isToday = data.date === nzNow().toLocaleDateString("en-CA");
  // Sites is absent on old cached responses (e.g. mid-deploy) -- fall back to the flat view rather
  // than crash. A single site has nothing to break down by, so it renders exactly as before.
  const sites = data.sites || [];
  const multiSite = sites.length > 1;
  return (
    <div className="rounded-2xl border border-[var(--hp-border)] bg-[var(--hp-card)] p-5 space-y-3" data-testid="daily-programme-bookings">
      <div className="flex flex-wrap items-center gap-3">
        <h3 className="font-head text-lg font-semibold text-[var(--hp-heading)]">{isToday ? "Today's bookings" : "Bookings"} · {dayLabel}</h3>
        <input data-testid="daily-bookings-date" type="date" value={date} onChange={(e) => setDate(e.target.value)}
          className="rounded-full border border-[var(--hp-border-hi)] bg-[var(--hp-bg)] px-3 py-1.5 text-xs font-semibold text-[var(--hp-heading)] outline-none" />
        {fetchedAt && <span className="text-xs text-[var(--hp-muted)]" data-testid="daily-bookings-asof" title="This doesn't auto-refresh — other screens (kiosk, rolls) may have moved on since">as of {fetchedAt.toLocaleTimeString("en-NZ", { hour: "2-digit", minute: "2-digit" })}</span>}
        <button data-testid="daily-bookings-refresh" onClick={load} title="Refresh" className="grid h-6 w-6 place-items-center rounded-full text-[var(--hp-muted)] hover:text-[var(--hp-heading)] hover:bg-[var(--hp-tint-5)]"><RefreshCw className="h-3.5 w-3.5" /></button>
        <span className="ml-auto text-xs text-[var(--hp-muted)]"><span className="font-mono-num font-semibold text-[var(--hp-heading)]" data-testid="daily-bookings-total">{data.total}</span> children booked</span>
        {onOpenRoll && <button data-testid="daily-bookings-open-roll" onClick={onOpenRoll} className="rounded-full border border-[var(--hp-border-hi)] px-3 py-1 text-xs font-semibold text-[var(--hp-heading)] hover:bg-[var(--hp-tint-5)]">Open the roll →</button>}
      </div>
      {data.total === 0 ? (
        <p className="text-sm text-[var(--hp-muted)]" data-testid="daily-bookings-empty">No confirmed bookings on this day.</p>
      ) : multiSite ? (
        <div className="space-y-4">
          {sites.map((site) => (
            <div key={site.siteId || "unassigned"} data-testid={`daily-bookings-site-${(site.siteId || "unassigned")}`}>
              <div className="flex items-center gap-2 mb-2">
                <MapPin className="h-3.5 w-3.5 text-[var(--hp-profit)]" />
                <span className="text-xs font-semibold uppercase tracking-wider text-[var(--hp-heading)]">{site.siteName}</span>
                <span className="font-mono-num text-xs font-semibold text-[var(--hp-muted)]">{site.total} booked</span>
              </div>
              <ProgrammeBookingCards programmes={site.programmes} prefix={`daily-bookings-${site.siteId || "unassigned"}`} />
            </div>
          ))}
        </div>
      ) : (
        <ProgrammeBookingCards programmes={data.programmes} prefix="daily-bookings" />
      )}
    </div>
  );
}
